import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { MutableRefObject } from "react";
import { updateReports } from "../../../../../api/postCall/updateReports";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { AllCommentsProps } from "../../../../../data-model/busniessOfficeNote";
import { User } from "../../../../../data-model/firebaseAuthModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { BusniessOfficeReportType } from "../../../../../enums/busniessOfficeReportType";
import { ScreenNamesForBigQuery } from "../../../../../enums/eventsData";
import { EventsName } from "../../../../../enums/eventsName";
import { insertNormalNotesAddedTrailComment } from "../../../../../utils/orderTrailComment";
import { getOrgIdForFetchExternalWorklist } from "../../../../../utils/organizationHelper";
import { showApiError } from "../../../../../utils/showApiError";
import { apiWorkerManager } from "../../../../../workerManagers/workerManager";
import { getAllJCodesFromAuthOnFile } from "../../../utils/existingDocsToLs";

/**
 * Handles saving business office (BO) notes for a medical PA order.
 *
 * Aborts any previously in-flight save via the caller-owned
 * `abortControllerRef`, then creates a fresh AbortController whose
 * signal is forwarded to `updateReports`. Success/failure is signalled by
 * whether `updateReports` throws (HTTP-status contract); on failure,
 * `showApiError` surfaces `body.detail` for 4xx and
 * `BO_NOTES_SAVED_FAILURE` for 5xx / non-HTTP errors. Aborts are silently
 * swallowed (no toast).
 *
 * @param generalOrderDetails - Flattened order details from Postgres.
 * @param authOnFileDetails - Auth-on-file record for J-code extraction.
 * @param user - The authenticated user performing the save action.
 * @param payerName - Payer name for analytics context.
 * @param globalBoValue - The global business office value/notes content.
 * @param allCommentsForUI - Array of all comments to be processed and saved.
 * @param providerName - Display name of the acting provider for trail comments.
 * @param setSaveBoLoading - Callback to update the loading state in the UI.
 * @param abortControllerRef - Mutable ref owned by the caller; replaced on
 *   every invocation so the caller can abort from outside (e.g. on unmount).
 * @param fetchAll - Reloads order + related slices after a successful `updateReports` call
 * @returns Resolves when the save attempt completes (success, failure, or abort)
 */
export const handleSaveBoNotes = async (
  generalOrderDetails: GeneralOrderDetails,
  authOnFileDetails: AuthOnFile,
  user: User,
  payerName: string,
  globalBoValue: string,
  allCommentsForUI: AllCommentsProps[],
  providerName: string,
  setSaveBoLoading: (value: boolean) => void,
  abortControllerRef: MutableRefObject<AbortController | null>,
  fetchAll: (orderId: string, orgId: string) => Promise<void>,
  siblingOrderIds: string[],
) => {
  abortControllerRef.current?.abort();
  const controller = new AbortController();
  abortControllerRef.current = controller;

  setSaveBoLoading(true);
  try {
    apiWorkerManager.enqueueAndProcess({
      patient_id:
        generalOrderDetails?.demographics?.patient_id ??
        generalOrderDetails?.demographics?.patient_mrn ??
        "",
      order_id: generalOrderDetails?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() || "",
      event_name: EventsName.SAVE_BO_NOTES_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.BUSNIESS_OFFICE_SCREEN,
        payer_name: payerName,
        j_codes: getAllJCodesFromAuthOnFile(authOnFileDetails ?? {}),
        bo_value: globalBoValue,
        comments: allCommentsForUI,
      },
    });

    const postComments = allCommentsForUI.filter(
      (comment) => comment.type === BusniessOfficeReportType.FINAL,
    );

    const reports = postComments.flatMap((commentGroup) =>
      commentGroup.comments.map((comment) => ({
        report_identifier: comment.id,
        template_text: comment.writeBackComment || "",
        report_status: comment.boStatus || "",
      })),
    );

    const payload = {
      order_id: generalOrderDetails?.order_id ?? "",
      reports: reports,
      org_id: getOrgIdForFetchExternalWorklist() || "",
      sibling_order_ids: siblingOrderIds,
    };

    await updateReports(payload, controller.signal);

    insertNormalNotesAddedTrailComment({
      orderId: generalOrderDetails?.order_id ?? "",
      orgId: getOrgIdForFetchExternalWorklist() || "",
      patientId:
        generalOrderDetails?.demographics?.patient_id ??
        generalOrderDetails?.demographics?.patient_mrn,
      providerEmail: user?.email ?? "",
      providerName,
      noteCount: reports.length,
    });

    toast.success(ToastMessages.BO_NOTES_SAVED_SUCCESS);
    fetchAll(
      generalOrderDetails?.order_id ?? "",
      getOrgIdForFetchExternalWorklist() || "",
    );
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error("Error saving BO notes:", error);
      showApiError(error, ToastMessages.BO_NOTES_SAVED_FAILURE);
    }
  } finally {
    if (abortControllerRef.current === controller) {
      abortControllerRef.current = null;
    }
    setSaveBoLoading(false);
  }
};
